import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../../context/AuthContext'

// Cross-instance dedup flag so multiple <TrialBanner /> mounts (e.g. when the
// per-page V1 imports coexist with a new layout-level render from
// ProtectedRoute) don't stack duplicate banners. First mounter claims primary
// and holds it for its lifetime; others render null.
let _hasPrimary = false

// ─── TrialBanner ─────────────────────────────────────────────────────────────
//
// Two modes, driven by AuthContext signals:
//
// 1. Informational ("N days left in trial") — unpaid users during the early
//    trial window (day 0-9 AND < 3 events). Low-friction reminder.
//
// 2. Retrospective-value nudge — fires once `upgradePromptDue` is true
//    (day >= 10 OR event count >= 3). Pitches the upgrade as protecting the
//    work the planner has already put in. Links straight to /upgrade/v2
//    which shows the actual retrospective stats.
//
// The hard-block (day > 14) is handled by ProtectedRoute; this banner never
// blocks navigation. Dismissals are session-scoped so the nudge reappears on
// each new session — we want the planner to see it, not forget it.
//
// Subscribed users see nothing.

const DISMISS_KEY_INFO   = 'trial_banner_dismissed_info_v1'
const DISMISS_KEY_NUDGE  = 'trial_banner_dismissed_nudge_v1'

export function TrialBanner() {
  const navigate = useNavigate()
  const {
    user, loading,
    accessBlocked, subscribed,
    upgradePromptDue, trialDaysLeft, paywallEventCount,
  } = useAuthContext()

  // Which dismissal key we're using depends on the current mode — dismiss-once
  // per mode, so if a planner dismisses the info banner and then later rolls
  // into the nudge window, we show the nudge anyway.
  const mode = !user || loading || subscribed || accessBlocked
    ? null
    : upgradePromptDue ? 'nudge' : 'info'

  const dismissKey = mode === 'nudge' ? DISMISS_KEY_NUDGE
                    : mode === 'info' ? DISMISS_KEY_INFO
                    : null

  const [dismissed, setDismissed] = useState(() => {
    if (!dismissKey) return false
    try { return sessionStorage.getItem(dismissKey) === '1' } catch { return false }
  })

  // Dedup across multiple simultaneous mounts — first-in claims primary and
  // holds it until unmount; other instances render null.
  const isPrimaryRef = useRef(null)
  if (isPrimaryRef.current === null) {
    isPrimaryRef.current = !_hasPrimary
    // eslint-disable-next-line react-hooks/globals
    if (isPrimaryRef.current) _hasPrimary = true
  }
  useEffect(() => {
    return () => { if (isPrimaryRef.current) _hasPrimary = false }
  }, [])
  const isPrimary = isPrimaryRef.current === true

  // When the mode changes (planner crosses into the nudge window during a
  // session), reset the dismissed flag so the new banner gets its own shot.
  useEffect(() => {
    if (!dismissKey) return
    try { setDismissed(sessionStorage.getItem(dismissKey) === '1') } catch { setDismissed(false) }
  }, [dismissKey])

  if (!mode || dismissed || !isPrimary) return null

  const handleDismiss = () => {
    setDismissed(true)
    try { sessionStorage.setItem(dismissKey, '1') } catch { /* ignore */ }
  }

  const goToUpgrade = () => navigate('/upgrade/v2')

  // ── Retrospective-value nudge ─────────────────────────────────────────────
  if (mode === 'nudge') {
    const reason = (paywallEventCount >= 3)
      ? `You've planned ${paywallEventCount} events with PlanOS`
      : `You've been building in PlanOS for ${14 - (trialDaysLeft ?? 0)} days`
    return (
      <div
        data-ns
        className="w-full flex items-center justify-between px-5 py-2.5 text-sm"
        style={{
          background: 'var(--accent)',
          color: '#fff',
        }}
      >
        <div className="flex-1 text-center">
          <span className="font-semibold">✦ {reason}.</span>
          <span className="mx-2 opacity-80">Lock in your founder plan before the trial ends.</span>
          <button
            onClick={goToUpgrade}
            className="ml-3 font-semibold underline hover:opacity-90 transition-opacity"
            style={{ color: '#fff' }}
          >
            See what you've built →
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-4 opacity-80 hover:opacity-100 text-lg leading-none flex-shrink-0"
          style={{ color: '#fff' }}
          aria-label="Dismiss banner"
        >
          ×
        </button>
      </div>
    )
  }

  // ── Informational (low-touch "days left in trial") ─────────────────────────
  const remaining = trialDaysLeft ?? 14
  return (
    <div
      data-ns
      className="w-full flex items-center justify-between px-5 py-2.5 text-sm"
      style={{ backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
    >
      <div className="flex-1 text-center">
        <span className="font-medium">
          {remaining} day{remaining === 1 ? '' : 's'} left in your trial
        </span>
        <span className="mx-2 opacity-60">—</span>
        <span className="opacity-80">Full access to every feature.</span>
        <button
          onClick={goToUpgrade}
          className="ml-3 font-semibold underline hover:opacity-80 transition-opacity"
          style={{ color: 'var(--paper)' }}
        >
          Choose a plan →
        </button>
      </div>
      <button
        onClick={handleDismiss}
        className="ml-4 opacity-60 hover:opacity-100 text-lg leading-none flex-shrink-0"
        style={{ color: 'var(--paper)' }}
        aria-label="Dismiss banner"
      >
        ×
      </button>
    </div>
  )
}
